import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

const VendorsTab = ({ rawsId }) => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [formData, setFormData] = useState({
        vendor_id: null,
        vendor_name: '',
        vendor_sku: '',
        unit_cost: '',
        lead_time_days: '',
        moq: '',
        is_preferred: 0,
        notes: ''
    });

    const fetchVendors = useCallback(async () => {
        try {
            const response = await axios.get(`raws/${rawsId}/vendors`);
            setVendors(response.data.vendors || []);
        } catch (error) {
            console.error('Error fetching vendors:', error);
        } finally {
            setLoading(false);
        }
    }, [rawsId]);

    useEffect(() => {
        if (rawsId) fetchVendors();
    }, [rawsId, fetchVendors]);

    // Debounced vendor search
    useEffect(() => {
        if (searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const response = await axios.get(`raws/vendors/search?q=${encodeURIComponent(searchQuery)}`);
                setSearchResults(response.data.vendors || []);
            } catch (error) {
                console.error('Error searching vendors:', error);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const openAddModal = () => {
        setEditingId(null);
        setFormData({ vendor_id: null, vendor_name: '', vendor_sku: '', unit_cost: '', lead_time_days: '', moq: '', is_preferred: 0, notes: '' });
        setSearchQuery('');
        setSearchResults([]);
        setShowModal(true);
    };

    const openEditModal = (vp) => {
        setEditingId(vp.id);
        setFormData({
            vendor_id: vp.vendor_id,
            vendor_name: `${vp.vendor_name || ''} - ${vp.vendor_company || ''}`,
            vendor_sku: vp.vendor_sku || '',
            unit_cost: vp.unit_cost != null ? vp.unit_cost.toString() : '',
            lead_time_days: vp.lead_time_days != null ? vp.lead_time_days.toString() : '',
            moq: vp.moq || '',
            is_preferred: vp.is_preferred || 0,
            notes: vp.notes || ''
        });
        setSearchQuery('');
        setSearchResults([]);
        setShowModal(true);
    };

    const selectVendor = (vendor) => {
        setFormData(prev => ({
            ...prev,
            vendor_id: vendor.id,
            vendor_name: `${vendor.vendor} - ${vendor.company || ''}`
        }));
        setSearchQuery('');
        setSearchResults([]);
    };

    const handleSave = async () => {
        if (!formData.vendor_id) {
            toast.error('Please select a vendor');
            return;
        }

        try {
            const payload = {
                vendor_sku: formData.vendor_sku || null,
                unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
                lead_time_days: formData.lead_time_days ? parseInt(formData.lead_time_days) : null,
                moq: formData.moq || null,
                is_preferred: formData.is_preferred,
                notes: formData.notes || null
            };

            if (editingId) {
                await axios.put(`raws/${rawsId}/vendors/${editingId}`, payload);
                toast.success('Vendor association updated');
            } else {
                payload.vendor_id = formData.vendor_id;
                await axios.post(`raws/${rawsId}/vendors`, payload);
                toast.success('Vendor associated successfully');
            }

            setShowModal(false);
            fetchVendors();
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error('This vendor is already associated with this part');
            } else {
                toast.error('Failed to save vendor association');
                console.error('Error saving vendor:', error);
            }
        }
    };

    const handleDelete = async (vendorPartId, vendorName) => {
        if (!window.confirm(`Remove vendor "${vendorName}" from this part? This does not delete the vendor.`)) return;

        try {
            await axios.delete(`raws/${rawsId}/vendors/${vendorPartId}`);
            toast.success('Vendor association removed');
            fetchVendors();
        } catch (error) {
            toast.error('Failed to remove vendor');
            console.error('Error deleting vendor:', error);
        }
    };

    if (loading) {
        return <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>Loading vendors...</div>;
    }

    return (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h4 style={{ fontWeight: '600', margin: 0, color: '#333' }}>Vendors</h4>
                <button className="btn btn-primary btn-sm" onClick={openAddModal}>
                    <i className="ti-plus" style={{ marginRight: '6px' }}></i>
                    Add Vendor
                </button>
            </div>

            {vendors.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
                    <i className="ti-truck" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                    <p className="mt-3 mb-0">No vendors associated with this part. Click "Add Vendor" to add one.</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead style={{ background: '#f8f9fa' }}>
                            <tr>
                                <th style={{ fontWeight: '600', padding: '12px 16px', border: 'none' }}>Vendor Name</th>
                                <th style={{ fontWeight: '600', padding: '12px 16px', border: 'none' }}>Company</th>
                                <th style={{ fontWeight: '600', padding: '12px 16px', border: 'none' }}>SKU</th>
                                <th style={{ fontWeight: '600', padding: '12px 16px', border: 'none' }}>Unit Cost</th>
                                <th style={{ fontWeight: '600', padding: '12px 16px', border: 'none' }}>Lead Time</th>
                                <th style={{ fontWeight: '600', padding: '12px 16px', border: 'none' }}>MOQ</th>
                                <th style={{ fontWeight: '600', padding: '12px 16px', border: 'none', textAlign: 'center' }}>Preferred</th>
                                <th style={{ fontWeight: '600', padding: '12px 16px', border: 'none', textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vendors.map((vp) => (
                                <tr key={vp.id}>
                                    <td style={{ padding: '12px 16px', border: 'none' }}>{vp.vendor_name}</td>
                                    <td style={{ padding: '12px 16px', border: 'none' }}>{vp.vendor_company}</td>
                                    <td style={{ padding: '12px 16px', border: 'none' }}>{vp.vendor_sku || '—'}</td>
                                    <td style={{ padding: '12px 16px', border: 'none' }}>{vp.unit_cost != null ? `$${vp.unit_cost.toFixed(2)}` : '—'}</td>
                                    <td style={{ padding: '12px 16px', border: 'none' }}>{vp.lead_time_days != null ? `${vp.lead_time_days} days` : '—'}</td>
                                    <td style={{ padding: '12px 16px', border: 'none' }}>{vp.moq || '—'}</td>
                                    <td style={{ padding: '12px 16px', border: 'none', textAlign: 'center' }}>
                                        {vp.is_preferred ? (
                                            <span style={{ color: '#f59e0b', fontSize: '18px' }}>&#9733;</span>
                                        ) : '—'}
                                    </td>
                                    <td style={{ padding: '12px 16px', border: 'none', textAlign: 'center' }}>
                                        <button className="btn btn-sm btn-outline-primary" style={{ marginRight: '6px' }} onClick={() => openEditModal(vp)} title="Edit">
                                            <i className="ti-pencil"></i>
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(vp.id, vp.vendor_name)} title="Remove">
                                            <i className="ti-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add/Edit Vendor Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingId ? 'Edit Vendor Association' : 'Add Vendor'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!editingId && (
                        <Form.Group className="mb-3">
                            <Form.Label>Search Vendor</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Type vendor name or company..."
                                value={formData.vendor_id ? formData.vendor_name : searchQuery}
                                onChange={(e) => {
                                    if (formData.vendor_id) {
                                        setFormData(prev => ({ ...prev, vendor_id: null, vendor_name: '' }));
                                    }
                                    setSearchQuery(e.target.value);
                                }}
                            />
                            {searching && <small className="text-muted">Searching...</small>}
                            {searchResults.length > 0 && !formData.vendor_id && (
                                <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto', marginTop: '4px' }}>
                                    {searchResults.map(v => (
                                        <div key={v.id}
                                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                                            onClick={() => selectVendor(v)}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        >
                                            <strong>{v.vendor}</strong> — {v.company || 'N/A'}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {formData.vendor_id && (
                                <small className="text-success">Selected: {formData.vendor_name}</small>
                            )}
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Vendor SKU / Part Number</Form.Label>
                        <Form.Control type="text" value={formData.vendor_sku} onChange={(e) => setFormData(prev => ({ ...prev, vendor_sku: e.target.value }))} />
                    </Form.Group>

                    <div className="row">
                        <div className="col-md-4">
                            <Form.Group className="mb-3">
                                <Form.Label>Unit Cost ($)</Form.Label>
                                <Form.Control type="number" step="0.01" value={formData.unit_cost} onChange={(e) => setFormData(prev => ({ ...prev, unit_cost: e.target.value }))} />
                            </Form.Group>
                        </div>
                        <div className="col-md-4">
                            <Form.Group className="mb-3">
                                <Form.Label>Lead Time (days)</Form.Label>
                                <Form.Control type="number" value={formData.lead_time_days} onChange={(e) => setFormData(prev => ({ ...prev, lead_time_days: e.target.value }))} />
                            </Form.Group>
                        </div>
                        <div className="col-md-4">
                            <Form.Group className="mb-3">
                                <Form.Label>MOQ</Form.Label>
                                <Form.Control type="text" value={formData.moq} onChange={(e) => setFormData(prev => ({ ...prev, moq: e.target.value }))} />
                            </Form.Group>
                        </div>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Preferred Vendor"
                            checked={formData.is_preferred === 1}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_preferred: e.target.checked ? 1 : 0 }))}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control as="textarea" rows={2} value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>{editingId ? 'Update' : 'Add Vendor'}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default VendorsTab;
